import { getGetConversationQueryKey } from '@roko/api/generated/conversations/conversations'
import type { TChatListItem, TChatMessage } from '@roko/ui/components/chat/types'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { type FC, type ReactNode, createContext, useCallback, useContext, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { useTranslation } from 'react-i18next'

import {
  createAnonConversation,
  getAnonConversation,
  resumeAnonPrompt,
  sendAnonPrompt,
  submitAnonSafetyGate,
} from '@/data/api/anonConversations'
import {
  useChatQuery,
  useChatsQuery,
  useCreateChatMutation,
  useDeleteChatMutation,
  useResumeChatPromptMutation,
  useSendChatPromptMutation,
} from '@/data/api/hooks/useChat'
import { getUserInitials, mapChatToListItem, mapChatToMessages } from '@/data/api/mappers/chat'
import { type TSafetyGateResponse, submitRegisteredSafetyGate } from '@/data/api/safetyGate'
import { getApiErrorMessage, isAnonChatDisabledError } from '@/data/api/types'
import { useAuthStore } from '@/store/authStore'

type TConversationGateStatus = {
  safety_gate_status?: string
}

type TChatContextValue = {
  chats: TChatListItem[]
  activeChatId?: string
  activeMessages: TChatMessage[]
  hasActiveConversation: boolean
  isLoadingChats: boolean
  isLoadingMessages: boolean
  isSending: boolean
  isEmergencyLock: boolean
  isDangerCheckPending: boolean
  error: string | null
  isAnonChatDisabled: boolean
  selectChat: (id: string) => void
  createChat: () => void
  deleteChat: (id: string) => void
  sendMessage: (text: string) => void
  submitSafetyGate: (imminentDanger: boolean) => Promise<TSafetyGateResponse>
  answerDangerCheck: (imminentDanger: boolean) => Promise<void>
  clearEmergencyLock: () => Promise<void>
  clearError: () => void
}

const ChatContext = createContext<TChatContextValue | null>(null)

const anonChatQueryKey = (id?: string) => ['anon-conversation', id] as const

export const ChatProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const userName = useAuthStore((state) => state.user?.userName)
  const isRegistered = useAuthStore((state) => state.isRegistered)
  const userInitials = useMemo(() => getUserInitials(userName), [userName])

  const [activeChatId, setActiveChatId] = useState<string | undefined>()
  const [pendingMessages, setPendingMessages] = useState<TChatMessage[]>([])
  /** Set from SSE [DANGER_CHECK]; kept until server status catches up or user answers. */
  const [streamDangerPending, setStreamDangerPending] = useState(false)
  const [streamEmergencyLock, setStreamEmergencyLock] = useState(false)
  const [isAnonChatDisabled, setIsAnonChatDisabled] = useState(false)

  const chatsQuery = useChatsQuery(isRegistered)
  const registeredChatQuery = useChatQuery(isRegistered ? activeChatId : undefined)
  const anonChatQuery = useQuery({
    queryKey: anonChatQueryKey(activeChatId),
    queryFn: () => getAnonConversation(activeChatId!),
    enabled: !isRegistered && Boolean(activeChatId),
  })

  const createChatMutation = useCreateChatMutation()
  const deleteChatMutation = useDeleteChatMutation()
  const sendChatPromptMutation = useSendChatPromptMutation()
  const resumeChatPromptMutation = useResumeChatPromptMutation()

  const createAnonMutation = useMutation({
    mutationFn: createAnonConversation,
    onSuccess: (conversation) => {
      queryClient.setQueryData(anonChatQueryKey(conversation.id), conversation)
    },
    onError: (error) => {
      if (isAnonChatDisabledError(error)) {
        setIsAnonChatDisabled(true)
      }
    },
  })

  const sendAnonMutation = useMutation({
    mutationFn: ({
      conversationId,
      content,
      onChunk,
      onEmergency,
      onDangerCheck,
    }: {
      conversationId: string
      content: string
      onChunk?: (chunk: string) => void
      onEmergency?: () => void
      onDangerCheck?: () => void
    }) => sendAnonPrompt({ conversationId, content, onChunk, onEmergency, onDangerCheck }),
    onSuccess: async (_void, { conversationId }) => {
      await queryClient.invalidateQueries({ queryKey: anonChatQueryKey(conversationId) })
    },
    onError: (error) => {
      if (isAnonChatDisabledError(error)) {
        setIsAnonChatDisabled(true)
      }
    },
  })

  const resumeAnonMutation = useMutation({
    mutationFn: ({
      conversationId,
      onChunk,
      onEmergency,
      onDangerCheck,
    }: {
      conversationId: string
      onChunk?: (chunk: string) => void
      onEmergency?: () => void
      onDangerCheck?: () => void
    }) => resumeAnonPrompt({ conversationId, onChunk, onEmergency, onDangerCheck }),
    onSuccess: async (_void, { conversationId }) => {
      await queryClient.invalidateQueries({ queryKey: anonChatQueryKey(conversationId) })
    },
  })

  const switchActiveChat = useCallback((id: string | undefined) => {
    setActiveChatId(id)
    setPendingMessages([])
    setStreamDangerPending(false)
    setStreamEmergencyLock(false)
  }, [])

  const wasRegisteredRef = useRef(isRegistered)

  useLayoutEffect(() => {
    if (wasRegisteredRef.current && !isRegistered) {
      switchActiveChat(undefined)
      void queryClient.cancelQueries({ queryKey: anonChatQueryKey(activeChatId) })
      queryClient.removeQueries({ queryKey: anonChatQueryKey(activeChatId) })
      createChatMutation.reset()
      deleteChatMutation.reset()
      sendChatPromptMutation.reset()
    }

    wasRegisteredRef.current = isRegistered
  }, [activeChatId, createChatMutation, deleteChatMutation, isRegistered, queryClient, sendChatPromptMutation, switchActiveChat])

  const chats = useMemo(() => {
    if (!isRegistered) {
      return []
    }

    return (chatsQuery.data ?? []).map((chat) => {
      const item = mapChatToListItem(chat)

      if (item.title === 'New chat') {
        return { ...item, title: t('chat.newChat') }
      }

      return item
    })
  }, [chatsQuery.data, isRegistered, t])

  const activeConversation = isRegistered ? registeredChatQuery.data : anonChatQuery.data
  const safetyGateStatus = (activeConversation as TConversationGateStatus | undefined)?.safety_gate_status

  // Prefer server status; keep stream flags so a stale cached "cleared" cannot hide Yes/No.
  const isDangerCheckPending = safetyGateStatus === 'pending' || streamDangerPending
  const isEmergencyLock = safetyGateStatus === 'emergency' || streamEmergencyLock

  const activeMessages = useMemo(() => {
    const messages = activeConversation ? mapChatToMessages(activeConversation, userInitials) : []

    return [...messages, ...pendingMessages]
  }, [activeConversation, pendingMessages, userInitials])

  const hasActiveConversation = activeMessages.length > 0

  const isCreating = isRegistered ? createChatMutation.isPending : createAnonMutation.isPending
  const isSending =
    (isRegistered
      ? sendChatPromptMutation.isPending || resumeChatPromptMutation.isPending
      : sendAnonMutation.isPending || resumeAnonMutation.isPending) || false

  const error = useMemo(() => {
    // Check for anon chat disabled first
    if (!isRegistered && isAnonChatDisabled) {
      return 'ANON_CHAT_DISABLED'  // Special marker
    }

    if (isRegistered && chatsQuery.isError) {
      return getApiErrorMessage(chatsQuery.error, t('chat.errors.loadChats'))
    }

    if ((isRegistered ? registeredChatQuery.isError : anonChatQuery.isError) && activeChatId) {
      return getApiErrorMessage(isRegistered ? registeredChatQuery.error : anonChatQuery.error, t('chat.errors.loadMessages'))
    }

    if (isRegistered ? createChatMutation.isError : createAnonMutation.isError) {
      return getApiErrorMessage(isRegistered ? createChatMutation.error : createAnonMutation.error, t('chat.errors.createChat'))
    }

    if (isRegistered && deleteChatMutation.isError) {
      return getApiErrorMessage(deleteChatMutation.error, t('chat.errors.deleteChat'))
    }

    if (isRegistered ? sendChatPromptMutation.isError : sendAnonMutation.isError) {
      return getApiErrorMessage(isRegistered ? sendChatPromptMutation.error : sendAnonMutation.error, t('chat.errors.sendMessage'))
    }

    return null
  }, [
    activeChatId,
    anonChatQuery.error,
    anonChatQuery.isError,
    chatsQuery.error,
    chatsQuery.isError,
    createAnonMutation.error,
    createAnonMutation.isError,
    createChatMutation.error,
    createChatMutation.isError,
    deleteChatMutation.error,
    deleteChatMutation.isError,
    isAnonChatDisabled,
    isRegistered,
    registeredChatQuery.error,
    registeredChatQuery.isError,
    sendAnonMutation.error,
    sendAnonMutation.isError,
    sendChatPromptMutation.error,
    sendChatPromptMutation.isError,
    t,
  ])

  const clearError = useCallback(() => {
    createChatMutation.reset()
    deleteChatMutation.reset()
    sendChatPromptMutation.reset()
    createAnonMutation.reset()
    sendAnonMutation.reset()
    setIsAnonChatDisabled(false)
  }, [createAnonMutation, createChatMutation, deleteChatMutation, sendAnonMutation, sendChatPromptMutation])

  const selectChat = useCallback(
    (id: string) => {
      switchActiveChat(id)
    },
    [switchActiveChat]
  )

  const createChat = useCallback(() => {
    if (isRegistered) {
      createChatMutation.mutate(
        { data: null },
        {
          onSuccess: (chat) => {
            switchActiveChat(chat.id)
          },
        }
      )
      return
    }

    createAnonMutation.mutate(undefined, {
      onSuccess: (chat) => {
        switchActiveChat(chat.id)
      },
    })
  }, [createAnonMutation, createChatMutation, isRegistered, switchActiveChat])

  const deleteChat = useCallback(
    (id: string) => {
      if (!isRegistered) {
        return
      }

      deleteChatMutation.mutate(
        { conversationId: id },
        {
          onSuccess: () => {
            if (activeChatId !== id) {
              return
            }

            const remainingChats = chats.filter((chat) => chat.id !== id)
            switchActiveChat(remainingChats[0]?.id)
          },
        }
      )
    },
    [activeChatId, chats, deleteChatMutation, isRegistered, switchActiveChat]
  )

  const sendMessage = useCallback(
    (text: string) => {
      if (isSending || isCreating) {
        return
      }

      const timestamp = Date.now()
      const pendingAssistantId = `pending-assistant-${timestamp}`
      setPendingMessages([
        {
          id: `pending-user-${timestamp}`,
          role: 'user',
          authorInitials: userInitials,
          content: text,
        },
        {
          id: pendingAssistantId,
          role: 'assistant',
          authorInitials: 'AI',
          isPending: true,
          content: '',
        },
      ])

      const sendToConversation = (conversationId: string) => {
        const onChunk = (chunk: string) => {
          flushSync(() => {
            setPendingMessages((current) =>
              current.map((message) =>
                message.id === pendingAssistantId ? { ...message, content: `${message.content ?? ''}${chunk}`, isPending: true } : message
              )
            )
          })
        }
        const onEmergency = () => {
          setStreamEmergencyLock(true)
          setStreamDangerPending(false)
        }
        const onDangerCheck = () => {
          setStreamDangerPending(true)
          setStreamEmergencyLock(false)
        }

        if (isRegistered) {
          sendChatPromptMutation.mutate(
            {
              conversationId,
              data: { content: text },
              onChunk,
              onEmergency,
              onDangerCheck,
            },
            { onSettled: () => setPendingMessages([]) }
          )
          return
        }

        sendAnonMutation.mutate(
          { conversationId, content: text, onChunk, onEmergency, onDangerCheck },
          { onSettled: () => setPendingMessages([]) }
        )
      }

      if (activeChatId) {
        sendToConversation(activeChatId)
        return
      }

      const onCreated = (chat: { id: string }) => {
        setActiveChatId(chat.id)
        sendToConversation(chat.id)
      }

      const onCreateFailed = () => {
        setPendingMessages([])
      }

      if (isRegistered) {
        createChatMutation.mutate({ data: null }, { onSuccess: onCreated, onError: onCreateFailed })
        return
      }

      createAnonMutation.mutate(undefined, { onSuccess: onCreated, onError: onCreateFailed })
    },
    [
      activeChatId,
      createAnonMutation,
      createChatMutation,
      isCreating,
      isRegistered,
      isSending,
      sendAnonMutation,
      sendChatPromptMutation,
      userInitials,
    ]
  )

  const submitSafetyGate = useCallback(
    async (imminentDanger: boolean): Promise<TSafetyGateResponse> => {
      const ensureConversationId = async (): Promise<string> => {
        if (activeChatId) {
          return activeChatId
        }

        if (isRegistered) {
          const chat = await createChatMutation.mutateAsync({ data: null })
          switchActiveChat(chat.id)
          return chat.id
        }

        const chat = await createAnonMutation.mutateAsync(undefined)
        switchActiveChat(chat.id)
        return chat.id
      }

      const conversationId = await ensureConversationId()
      const result = isRegistered
        ? await submitRegisteredSafetyGate(conversationId, imminentDanger)
        : await submitAnonSafetyGate(conversationId, imminentDanger)

      if (imminentDanger) {
        setStreamEmergencyLock(true)
        setStreamDangerPending(false)
      } else {
        setStreamEmergencyLock(false)
        setStreamDangerPending(false)
      }

      if (isRegistered) {
        await queryClient.invalidateQueries({ queryKey: getGetConversationQueryKey(conversationId) })
      } else {
        await queryClient.invalidateQueries({ queryKey: anonChatQueryKey(conversationId) })
      }

      return result
    },
    [activeChatId, createAnonMutation, createChatMutation, isRegistered, queryClient, switchActiveChat]
  )

  const answerDangerCheck = useCallback(
    async (imminentDanger: boolean) => {
      if (!activeChatId) {
        return
      }

      const result = await submitSafetyGate(imminentDanger)
      if (imminentDanger) {
        return
      }

      const timestamp = Date.now()
      const pendingAssistantId = `pending-assistant-resume-${timestamp}`
      setPendingMessages([
        {
          id: pendingAssistantId,
          role: 'assistant',
          authorInitials: 'AI',
          isPending: true,
          content: '',
        },
      ])

      const onChunk = (chunk: string) => {
        flushSync(() => {
          setPendingMessages((current) =>
            current.map((message) =>
              message.id === pendingAssistantId ? { ...message, content: `${message.content ?? ''}${chunk}`, isPending: true } : message
            )
          )
        })
      }

      try {
        if (isRegistered) {
          await resumeChatPromptMutation.mutateAsync({
            conversationId: activeChatId,
            onChunk,
          })
        } else {
          await resumeAnonMutation.mutateAsync({
            conversationId: activeChatId,
            onChunk,
          })
        }
      } finally {
        setPendingMessages([])
      }

      void result
    },
    [activeChatId, isRegistered, resumeAnonMutation, resumeChatPromptMutation, submitSafetyGate]
  )

  const clearEmergencyLock = useCallback(async () => {
    await submitSafetyGate(false)
  }, [submitSafetyGate])

  const value = useMemo(
    () => ({
      chats,
      activeChatId,
      activeMessages,
      hasActiveConversation,
      isLoadingChats: isRegistered && chatsQuery.isPending,
      isLoadingMessages: Boolean(activeChatId) && (isRegistered ? registeredChatQuery.isPending : anonChatQuery.isPending),
      isSending: isSending || isCreating,
      isEmergencyLock,
      isDangerCheckPending,
      error,
      isAnonChatDisabled,
      selectChat,
      createChat,
      deleteChat,
      sendMessage,
      submitSafetyGate,
      answerDangerCheck,
      clearEmergencyLock,
      clearError,
    }),
    [
      activeChatId,
      activeMessages,
      anonChatQuery.isPending,
      answerDangerCheck,
      chats,
      chatsQuery.isPending,
      clearEmergencyLock,
      clearError,
      createChat,
      deleteChat,
      error,
      hasActiveConversation,
      isAnonChatDisabled,
      isCreating,
      isDangerCheckPending,
      isEmergencyLock,
      isRegistered,
      isSending,
      registeredChatQuery.isPending,
      selectChat,
      sendMessage,
      submitSafetyGate,
    ]
  )

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>
}

export const useChatContext = (): TChatContextValue => {
  const context = useContext(ChatContext)
  if (!context) {
    throw new Error('useChatContext must be used within ChatProvider')
  }
  return context
}
